"use client";

import React, { useState } from "react";
import { Clock, ChartBar, Star, BookOpen } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { cn } from "@/lib/utils";
function CoursesGridConsumer() {
  const {
    products,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="222717" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="h-[450px] animate-pulse bg-muted/30 border-border" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-muted-foreground mb-4">Não foi possível carregar os projetos.</p>
        <Button onClick={refetch} variant="outline">Tentar novamente</Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <BookOpen className="size-12 text-muted-foreground mb-4 opacity-50" />
        <h3 className="text-xl font-default font-semibold text-foreground mb-2">Nenhum projeto encontrado</h3>
        <p className="text-muted-foreground">Tente selecionar outra categoria de portfólio.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <Card key={product.id} data-index={i} className="flex flex-col overflow-hidden transition-all duration-200 hover:-translate-y-[5px] hover:shadow-4xl border-border bg-card">
            {product.featured_image_url ? <div className="relative aspect-video overflow-hidden bg-muted">
                <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105 grayscale hover:grayscale-0" loading="lazy" />
                {product.categories?.[0] && <span className="absolute left-4 top-4 rounded-full border border-primary bg-background/90 px-3 py-1 text-xs font-medium text-primary backdrop-blur-sm">
                    {product.categories[0].name}
                  </span>}
              </div> : <div className="relative flex aspect-video items-center justify-center bg-muted/50">
                <BookOpen className="size-10 text-muted-foreground/50" />
              </div>}

            <CardHeader className="p-6 pb-4">
              <CardTitle className="line-clamp-2 text-2xl font-default font-bold text-foreground">
                {product.title}
              </CardTitle>
            </CardHeader>

            <CardContent className="flex-1 p-6 pt-0">
              <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Clock className="size-4 text-primary" />
                  <span>{product.customFields?.duration || "Objetivo"}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ChartBar className="size-4 text-primary" />
                  <span>{product.customFields?.difficulty || "Resultado"}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star className="size-4 text-primary fill-none" />
                  <span>{product.customFields?.rating || "Destaque"}</span>
                </div>
              </div>

              {product.short_description && <div className="line-clamp-3 text-sm text-muted-foreground [&>p]:mb-0" dangerouslySetInnerHTML={{
            __html: product.short_description
          }} />}
            </CardContent>

            <CardFooter className="flex flex-col gap-4 p-6 pt-0 mt-auto border-t border-border/50">
              <div className="flex items-center justify-between w-full pt-4">
                <span className="text-sm font-medium text-muted-foreground">Projeto:</span>
                <span className="text-xl font-default font-bold text-foreground">
                  {product.price !== undefined ? `R$ ${product.price}` : "Sob Consulta"}
                </span>
              </div>
              
              <div className="flex w-full gap-3">
                <Link to={product.link} className={cn(buttonVariants({
              variant: "outline"
            }), "flex-1 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 rounded-full")}>
                  Ver Projeto
                </Link>
                <div className="flex-1">
                  <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" text="Contratar" showQuantity={false} />
                </div>
              </div>
            </CardFooter>
          </Card>)}
      </div>

      {totalPages > 1 && <Pagination className="mt-12">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious onClick={e => {
            e.preventDefault();
            if (hasPrev) prevPage();
          }} className={cn("cursor-pointer", !hasPrev && "pointer-events-none opacity-50")} />
            </PaginationItem>
            <PaginationItem>
              <span className="px-4 text-sm font-medium text-muted-foreground">
                Página {currentPage} de {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext onClick={e => {
            e.preventDefault();
            if (hasNext) nextPage();
          }} className={cn("cursor-pointer", !hasNext && "pointer-events-none opacity-50")} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function Courses() {
  const [category, setCategory] = useState<string>("all");
  const wp_query_params: Record<string, any> = {
    per_page: 6,
    status: "publish"
  };
  if (category !== "all") {
    wp_query_params.product_cat = [parseInt(category, 10)];
  }
  const wp_query = new WP_Query(wp_query_params);
  return <section id="grade-de-projetos" className="w-full bg-background text-foreground py-20 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-center text-center">
          <h2 className="mb-4 text-3xl md:text-5xl font-default font-bold tracking-tight text-foreground">
            Nossos Projetos
          </h2>
          <p className="max-w-[700px] text-lg text-muted-foreground">
            Explore nossa seleção de trabalhos autorais. Cada projeto reflete nosso compromisso com a excelência técnica e a inovação institucional.
          </p>
        </div>

        <div className="mb-10 flex w-full justify-center overflow-x-auto pb-4">
          <ToggleGroup type="single" value={category} onValueChange={value => {
          if (value) setCategory(value);
        }} className="flex-nowrap md:flex-wrap gap-2">
            <ToggleGroupItem value="all" variant="outline" className="rounded-full px-6 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground whitespace-nowrap border-primary text-primary">
              Todos os Projetos
            </ToggleGroupItem>
            <ToggleGroupItem value="20" variant="outline" className="rounded-full px-6 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground whitespace-nowrap border-primary text-primary">
              Podcast Corporativo
            </ToggleGroupItem>
            <ToggleGroupItem value="21" variant="outline" className="rounded-full px-6 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground whitespace-nowrap border-primary text-primary">
              Produção de Vídeo
            </ToggleGroupItem>
            <ToggleGroupItem value="22" variant="outline" className="rounded-full px-6 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground whitespace-nowrap border-primary text-primary">
              Conteúdo Digital
            </ToggleGroupItem>
            <ToggleGroupItem value="23" variant="outline" className="rounded-full px-6 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground whitespace-nowrap border-primary text-primary">
              Captação em Estúdio
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <CoursesGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}